// Registry-related filters (Compliance → Registry list page).
//
// These are SEPARATE from the licenseFilters even though both ultimately
// resolve against the same `lm_registry` table. The license list filters
// (`licenseKeywords`, `licenseType`, etc.) target `client_license` rows
// joined to the registry; the registry list filters target the registry
// directly. Names mirror that distinction (`registry*`) and the registry
// service (`apps/crm-api/src/modules/licenses/services/registry.service.ts`)
// reads them off `RegistryQueryDto.filters` to build raw SQL — not via
// `applyFiltersToQueryBuilder`, since `lm_registry` has no TypeORM entity.

import {
	LICENSE_CATEGORIES,
	LICENSE_CATEGORY_LABEL,
	LICENSE_SOURCE_TYPE_LABEL,
	LICENSE_SOURCE_TYPES,
} from '@supply-mover-2-0/shared';
import { createKeywordFilter } from './factories';
import type { FilterRegistry } from './types';

const REGISTRY_LIST_KEYS: ReadonlyArray<string> = ['registry'];

export const registryFilters: FilterRegistry = {
	registryKeywords: {
		// FULLTEXT search backed by `idx_ft_search` on lm_registry. The
		// service does its own boolean-mode token sanitization so we don't
		// declare `fulltextFields` here — that would route through the
		// shared TypeORM filter builder which doesn't apply to raw SQL.
		//
		// `supportsRelevance` enables the framework's dynamic Relevance
		// sort option in the sort dropdown — only when this filter has a
		// non-empty value (see `useListData.ts::detectActiveRelevanceFilter`).
		// The factory's auto-set only fires for `fulltextIndex` (OpenSearch);
		// we use MySQL FULLTEXT directly, so we opt in explicitly. The
		// matching backend wiring lives in
		// `registry.service.ts::_resolveOrderBy` (D8).
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Business name, license #, city...',
			objectTable: 'lm_registry',
			listKeys: [...REGISTRY_LIST_KEYS],
			supportsRelevance: true,
		}),
		queryParamName: 'registryKeywords',
	},

	// Replaced the old `registryType` filter (D6 2026-05-27). That one was
	// backed by raw `lm_registry.license_type` — a varchar with 582 distinct
	// values, including ~65k+ rows of NPI taxonomy codes ('183500000X' etc.)
	// that an older scraper wrote into the wrong column. Unusable as a
	// dropdown. The two filters below split the same "what kind of license"
	// intent into a clean two-axis model:
	//   - `registrySourceType` — broad classification (STATE_BOARD / NPI / DEA / …),
	//     6 hardcoded enum values. Reads `lm_registry.source_type`.
	//   - `registryCategory`   — granular type (PHARMACIST / PHARMACY_FACILITY /
	//     CONTROLLED_SUBSTANCE_REGISTRATION / …), 26 hardcoded normalized
	//     values. Reads `lm_registry.license_category` (fully populated, 22
	//     distinct values in current data; the constants file declares 26 to
	//     cover values the normalization table can also emit).
	// Both follow the same hardcoded-options pattern as `registryStatus` — no
	// `/options` endpoint round-trip needed. See
	// `docs/claude/licensing-cleanup-defects.md` D6 for the analysis.
	registrySourceType: {
		type: 'multi-select',
		label: 'Source',
		placeholder: 'Select source...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		options: LICENSE_SOURCE_TYPES.map((value) => ({
			id: value,
			label: LICENSE_SOURCE_TYPE_LABEL[value],
		})),
		queryParamName: 'registrySourceType',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'lm_registry',
			fieldName: 'source_type',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...REGISTRY_LIST_KEYS],
	},

	registryCategory: {
		type: 'multi-select',
		label: 'Category',
		placeholder: 'Select category...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		options: LICENSE_CATEGORIES.map((value) => ({
			id: value,
			label: LICENSE_CATEGORY_LABEL[value],
		})),
		queryParamName: 'registryCategory',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'lm_registry',
			fieldName: 'license_category',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...REGISTRY_LIST_KEYS],
	},

	registryStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		// Static option set — matches the 7 buckets the normalization
		// migration writes to lm_registry.license_status_normalized (see
		// scripts/license-management/migrations/013-add-status-normalization.sql).
		// Mirrors the licenseStatus filter on the /licenses list so reps
		// trained on one surface can apply the same vocabulary on the other.
		options: [
			{ id: 'Active', label: 'Active' },
			{ id: 'Expired', label: 'Expired' },
			{ id: 'Pending', label: 'Pending' },
			{ id: 'Revoked', label: 'Revoked' },
			{ id: 'Suspended', label: 'Suspended' },
			{ id: 'Cancelled', label: 'Cancelled' },
			{ id: 'Other', label: 'Other' },
		],
		queryParamName: 'registryStatus',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'lm_registry',
			fieldName: 'license_status_normalized',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...REGISTRY_LIST_KEYS],
	},

	registryState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/registry/state/options',
		queryParamName: 'registryState',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'lm_registry',
			fieldName: 'state_code',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...REGISTRY_LIST_KEYS],
	},

	registryMatchStatus: {
		type: 'radio',
		label: 'Match status',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'registryMatchStatus',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'matched', label: 'Matched only' },
			{ id: 'unmatched', label: 'Unmatched only' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'lm_registry',
			joinType: 'custom',
		},
		// Convention parity with every other radio filter in the repo (see
		// licenseHasDisciplinary in license.ts). Today this works via
		// FilterService defaults; declaring it explicitly guards against a
		// future default change.
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [...REGISTRY_LIST_KEYS],
	},
};
